<template>
  <div class="bg-gray-100">
    <div class="min-vh-100">
      <div class="bg-white container mt-5">
        <div class="w-50 mx-auto mt-2">
          <transition name="fade">
            <MenssageAlert :messages="messages" />
          </transition>
        </div>
        <InlineNavigation
          routeBackName="automationStatistics"
          :routeBackParams="{ organizationId: this.$route.params.organizationId, workflowId: this.$route.params.workflowId }"
          :backRouteTranslation="`${$t('automation.statistics.nav.currentAutomation')}: ${this.workflowName || '-'}`"
          :currentRouteTranslation="`${this.statisticConfig.description || '-'}`"
          class="text-black pt-4"
        />
        <CContainer class="table-container container p-4">
          <CCol class="px-4 py-4 bg-white rounded-top d-flex justify-content-between table-header">
            <h2 class="text-black font-2xl font-weight-bold">
              {{ $t('automation.statistics.general.title') }}: {{ this.statisticConfig.description }}
            </h2>
          </CCol>
          <SenditTable
            class="mt-3 automationStatisticsPropertyTable"
            :headers="tableHeaders"
            :registers="tableRegisters"
            :loading="isLoadingWorkflowData || isLoadingMetricData"
            :itemsPerPage="pagination.itemsPerPage"
            :currentPage="pagination.currentPage"
            :totalPages="pagination.totalPages"
            :searchPlaceholder="getSearchPlaceholder"
            pagination
            searchable
            @search="handleSearchFilter"
            @updatePage="handleUpdatePage"
          >
            <template #filters-right>
              <div v-if="isCurrentOrganizationBase" class="d-flex justify-content-end">
                <CButton class="p-2 btn btn-primary" @click="handleExportContacts" :disabled="isExportButtonDisabled">
                  <span class="mr-2">{{ $t('automation.statistics.general.export.btn') }}</span>
                  <CSpinner v-if="isLoadingExportContacts" color="custom-color" style="width: 1.1rem; height: 1.1rem" />
                  <CIcon v-else name="arrowExport" class="text-white" size="custom-size" :height="25" viewBox="0 0 32 32" />
                </CButton>
              </div>
            </template>
            <template #email="props">
              <td>{{ props.item.email }}</td>
            </template>
            <template #openCount="props">
              <td>{{ props.item.openCount }}</td>
            </template>
            <template #clickCount="props">
              <td>{{ props.item.clickCount }}</td>
            </template>
            <template #sender="props">
              <td>{{ props.item.sender }}</td>
            </template>
            <template #sentDateTime="props">
              <td>{{ props.item.sentDateTime ? dateTimeFormatString(props.item.sentDateTime, null, null, false) : '-' }}</td>
            </template>
            <template #msisdn="props">
              <td>{{ props.item.msisdn }}</td>
            </template>
            <template #text="props">
              <td>{{ props.item.text }}</td>
            </template>
            <template #countryId="props">
              <td>
                <div class="d-flex">
                  <img :src="getFlag(props.item.countryId).flag" width="25" height="auto" class="mr-2" />
                  {{ getFlag(props.item.countryId).label }}
                </div>
              </td>
            </template>
            <template #deliveryReportDescription="props">
              <td>{{ props.item.deliveryReportDescription }}</td>
            </template>
            <template #totalSegments="props">
              <td>{{ props.item.totalSegments }}</td>
            </template>
            <template #deliveryReportUpdateDateTime="props">
              <td>
                {{ props.item.deliveryReportUpdateDateTime ? dateTimeFormatString(props.item.deliveryReportUpdateDateTime, null, null, false) : '-' }}
              </td>
            </template>
            <template #dlrStatus="props">
              <td>
                {{ getDLRStatus(props.item.dlrStatus) }}
              </td>
            </template>
          </SenditTable>
        </CContainer>
      </div>
    </div>
  </div>
</template>

<script>
import SenditTable from '@/components/table/SenditTable.vue'
import InlineNavigation from '../../../components/InlineNavigation.vue'
import MenssageAlert from '@/components/MenssageAlert'
import { STATISTICS_TYPES } from '../constants'
import { getErrorMessage } from '@/utils/errorHandling.js'
import services from '@/services'
import { dateTimeFormatString } from '../../../utils/dateUtil'
import { getFilenameFromContentDisposition } from '../../../utils/responseHeaderUtils'
import fileDownload from 'js-file-download'
import { CHANNEL_TYPES, DLR_STATUS } from '../constants'
import { nameCountries } from '@/utils/countriesUtils.js'
import { mapGetters } from 'vuex'

export default {
  name: 'AutomationStatisticsProperty',
  components: { InlineNavigation, MenssageAlert, SenditTable },
  data() {
    return {
      messages: [],
      workflowData: undefined,
      isLoadingWorkflowData: false,
      isLoadingMetricData: false,
      pagination: {
        itemsPerPage: 10,
        page: 1,
      },
      tableFilter: undefined,
      metricData: undefined,
      dateTimeFormatString: dateTimeFormatString,
      isLoadingExportContacts: false,
    }
  },
  mounted() {
    this.getPageData()
  },
  computed: {
    ...mapGetters({
      isOrganizationBase: 'auth/isOrganizationBase',
    }),

    isCurrentOrganizationBase() {
      return this.isOrganizationBase(this.$route.params.organizationId)
    },

    statisticConfig() {
      return this.statisticsConfigs[this.$route.params.statisticType]
    },
    statisticsConfigs() {
      return {
        [STATISTICS_TYPES.DELIVERED]: {
          description: this.$t('automation.statistics.general.card.type.001'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultSMSDelivered(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) => services.getCampaignResultSMSDeliveredDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.NOT_DELIVERED]: {
          description: this.$t('automation.statistics.general.card.type.002'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultSMSNotDelivered(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) =>
            services.getCampaignResultSMSNotDeliveredDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.PENDING]: {
          description: this.$t('automation.statistics.general.card.type.003'),
          fetchDataFn: (organizationId, campaignId, params) =>
            services.getCampaignResultSMSPending(organizationId, campaignId, { deliveryReportUpdateDateTimeHasValue: true, ...params }),
          downloadDataFn: (organizationId, campaignId, params) => services.getCampaignResultSMSPendingDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.OPEN]: {
          description: this.$t('automation.statistics.general.card.type.004'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailUniqueOpens(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) =>
            services.getCampaignResultEmailUniqueOpensDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.CLICK]: {
          description: this.$t('automation.statistics.general.card.type.005'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailClicks(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailClicksDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.BOUNCED]: {
          description: this.$t('automation.statistics.general.card.type.006'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailBounces(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailBouncesDownload(organizationId, campaignId, params),
        },
        [STATISTICS_TYPES.UNSUBSCRIBE]: {
          description: this.$t('automation.statistics.general.card.type.007'),
          fetchDataFn: (organizationId, campaignId, params) => services.getCampaignResultEmailUnsubscribes(organizationId, campaignId, params),
          downloadDataFn: (organizationId, campaignId, params) =>
            services.getCampaignResultEmailUnsubscribesDownload(organizationId, campaignId, params),
        },
      }
    },
    workflowName() {
      return this.workflowData?.name
    },
    workflowChannelType() {
      return this.workflowData?.workflowJson?.steps?.values?.[0]?.type
    },
    tableHeaders() {
      if (this.workflowChannelType == CHANNEL_TYPES.SEND_EMAIL) {
        const headers = [{ key: 'email', label: this.$t('automation.statistics.email.property.table.header.001') }]

        const isStatisticTypeBounced = this.$route.params.statisticType === STATISTICS_TYPES.BOUNCED

        if (isStatisticTypeBounced) {
          headers.push({ key: 'bounced', label: this.$t('automation.statistics.email.property.table.header.012') })
        } else {
          headers.push({ key: 'openCount', label: this.$t('automation.statistics.email.property.table.header.002') })
          headers.push({ key: 'clickCount', label: this.$t('automation.statistics.email.property.table.header.003') })
        }

        headers.push(
          { key: 'sender', label: this.$t('automation.statistics.email.property.table.header.004') },
          { key: 'sentDateTime', label: this.$t('automation.statistics.email.property.table.header.005') },
        )

        if (isStatisticTypeBounced) {
          headers.push({ key: 'error', label: this.$t('automation.statistics.email.property.table.header.014') })
          headers.push({ key: 'errorRelatedTo', label: this.$t('automation.statistics.email.property.table.header.013') })
        }

        return headers
      }
      if (this.workflowChannelType == CHANNEL_TYPES.SEND_SMS) {
        return [
          { key: 'msisdn', label: this.$t('automation.statistics.email.property.table.header.006') },
          { key: 'countryId', label: this.$t('automation.statistics.email.property.table.header.country') },
          { key: 'sender', label: this.$t('automation.statistics.email.property.table.header.004') },
          { key: 'text', label: this.$t('automation.statistics.email.property.table.header.007') },
          { key: 'sentDateTime', label: this.$t('automation.statistics.email.property.table.header.005') },
          { key: 'dlrStatus', label: this.$t('automation.statistics.email.property.table.header.011') },
          { key: 'deliveryReportUpdateDateTime', label: this.$t('automation.statistics.email.property.table.header.008') },
          { key: 'deliveryReportDescription', label: this.$t('automation.statistics.email.property.table.header.009') },
          { key: 'totalSegments', label: this.$t('automation.statistics.email.property.table.header.010') },
        ]
      }
    },
    tableRegisters() {
      if (!this.metricData || this.metricData.recipients.length == 0) return []

      return this.metricData.recipients
    },
    hasTableRegisters() {
      return this.tableRegisters.length > 0
    },
    isExportButtonDisabled() {
      return this.isLoadingWorkflowData || this.isLoadingMetricData || this.isLoadingExportContacts || !this.hasTableRegisters
    },
    getSearchPlaceholder() {
      if (this.workflowChannelType === CHANNEL_TYPES.SEND_SMS) return this.$t('automation.statistics.sms.search.placeholder')
      if (this.workflowChannelType === CHANNEL_TYPES.SEND_EMAIL) return this.$t('automation.statistics.email.search.placeholder')
    },
  },
  methods: {
    async getPageData() {
      await this.getWorkflowData()
      await this.getMetricData()
    },
    async getWorkflowData() {
      try {
        this.isLoadingWorkflowData = true
        const response = await services.getAutomation(this.$route.params.organizationId, this.$route.params.workflowId)
        this.workflowData = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingWorkflowData = false
      }
    },
    async getMetricData() {
      try {
        this.isLoadingMetricData = true
        const params = {
          page: this.pagination.page,
          count: this.pagination.itemsPerPage,
        }

        if (this.tableFilter) {
          const paramChannelMap = {
            [CHANNEL_TYPES.SEND_SMS]: 'msisdn',
            [CHANNEL_TYPES.SEND_EMAIL]: 'email',
          }

          const paramName = paramChannelMap[this.workflowChannelType]
          if (paramName) {
            params[paramName] = this.tableFilter
          }
        }

        const campaignId = this.workflowData.campaigns[0].campaignId
        const response = await this.statisticConfig.fetchDataFn(this.$route.params.organizationId, campaignId, params)

        this.pagination = {
          ...this.pagination,
          ...response.data.data.pagination,
        }

        this.metricData = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingMetricData = false
      }
    },
    getDLRStatus(dlrStatus) {
      switch (dlrStatus) {
        case DLR_STATUS.DELIVERED:
          return this.$t(`automation.statistics.general.sms.dlrStatus.delivered`)
        case DLR_STATUS.PENDING:
          return this.$t(`automation.statistics.general.sms.dlrStatus.pending`)
        case DLR_STATUS.NOT_DELIVERED:
          return this.$t(`automation.statistics.general.sms.dlrStatus.not-delivered`)
        default:
          return '-'
      }
    },
    async handleExportContacts() {
      try {
        this.isLoadingExportContacts = true
        const campaignId = this.workflowData.campaigns[0].campaignId
        const response = await this.statisticConfig.downloadDataFn(this.$route.params.organizationId, campaignId)
        const nameFile = getFilenameFromContentDisposition(response.headers['content-disposition'])

        fileDownload(response.data, nameFile, response.headers['content-type'])
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingExportContacts = false
      }
    },
    handleSearchFilter(filter) {
      this.pagination.page = 1
      this.tableFilter = filter
      this.getMetricData()
    },
    handleUpdatePage(page) {
      this.pagination.page = page
      this.getMetricData()
    },
    getFlag(data) {
      const item = nameCountries(this).filter((item) => item.value.toUpperCase() == data.toUpperCase())
      if (item[0]) {
        return { ...item[0] }
      }
      return ''
    },
  },
}
</script>
